import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  language as currentLanguage,
  setIsRTL,
  setLanguage,
} from "@common/helpers/i18n";

export type DocumentDirection = "rtl" | "ltr";

type DirectionState = {
  direction: DocumentDirection;
  isRtl: boolean;
  language: string;
};

type DirectionProviderProps = {
  children: ReactNode;
};

const rtlLanguagePattern = /^(fa|ar|he|ur)(-|$)/i;
const defaultLanguage = "fa-IR";

const DirectionContext = createContext<DirectionState | undefined>(undefined);

export function getDirectionFromLanguage(language?: string): DocumentDirection {
  return rtlLanguagePattern.test(language || "") ? "rtl" : "ltr";
}

function getDocumentLanguage() {
  if (typeof document === "undefined") {
    return currentLanguage || defaultLanguage;
  }

  return (
    document.documentElement.lang ||
    document.body.lang ||
    currentLanguage ||
    defaultLanguage
  );
}

export function getDocumentDirection(): DocumentDirection {
  if (typeof document === "undefined") {
    return getDirectionFromLanguage(currentLanguage || defaultLanguage);
  }

  const explicitDir = document.documentElement.dir || document.body.dir;
  if (explicitDir === "rtl" || explicitDir === "ltr") {
    return explicitDir;
  }

  return getDirectionFromLanguage(getDocumentLanguage());
}

function getDirectionState(): DirectionState {
  const direction = getDocumentDirection();

  return {
    direction,
    isRtl: direction === "rtl",
    language: getDocumentLanguage(),
  };
}

function isSameDirectionState(a: DirectionState, b: DirectionState) {
  return (
    a.direction === b.direction &&
    a.isRtl === b.isRtl &&
    a.language === b.language
  );
}

export function syncDocumentDirection({
  direction,
  language,
}: {
  direction?: DocumentDirection | boolean;
  language?: string;
} = {}) {
  const nextLanguage = language || getDocumentLanguage();
  const nextDirection: DocumentDirection =
    typeof direction === "boolean"
      ? direction
        ? "rtl"
        : "ltr"
      : direction || getDirectionFromLanguage(nextLanguage);

  if (typeof document !== "undefined") {
    document.documentElement.dir = nextDirection;
    document.body.dir = nextDirection;
    document.documentElement.lang = nextLanguage;
    document.body.lang = nextLanguage;
  }

  setIsRTL(nextDirection === "rtl");
  setLanguage(nextLanguage);

  return {
    direction: nextDirection,
    isRtl: nextDirection === "rtl",
    language: nextLanguage,
  };
}

export function DirectionProvider({ children }: DirectionProviderProps) {
  const [state, setState] = useState(() => syncDocumentDirection());

  useEffect(() => {
    const updateDirection = () => {
      const nextState = getDirectionState();
      setState((previousState) =>
        isSameDirectionState(previousState, nextState)
          ? previousState
          : nextState,
      );
    };

    updateDirection();

    const observer = new MutationObserver(updateDirection);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["dir", "lang"],
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir", "lang"],
    });

    return () => observer.disconnect();
  }, []);

  const value = useMemo(
    () => ({
      direction: state.direction,
      isRtl: state.isRtl,
      language: state.language,
    }),
    [state.direction, state.isRtl, state.language],
  );

  return createElement(DirectionContext.Provider, { value }, children);
}

export function useDirection() {
  return useContext(DirectionContext) || getDirectionState();
}

export function useDocumentDirection() {
  return useDirection().direction;
}

export function useIsRtl() {
  return useDirection().isRtl;
}
